/**
 * Display number and date formatting for the ZERO content area.
 *
 * - Latin digits in every UI language (`-u-nu-latn`) with the locale's own
 *   grouping, so figures read the same in Arabic, English and Indic UIs.
 * - Dates use the Gregorian calendar in every language (plain `ar` would
 *   otherwise fall back to the Islamic calendar in some engines).
 * - Figure and unit are returned separately (the unit is an i18n key) so the
 *   editorial KPI layout can style them independently.
 * - Pure module (no i18n import) so it runs in node-only vitest.
 *
 * PDF exports keep their own formatters (`utils/dashboardReportPdf.helpers.ts`);
 * do not route them here.
 */

export const EMPTY_VALUE = '—';

/** U+2212 MINUS SIGN — typographically matches "+" in tabular figures. */
const MINUS = '−';

const LOCALE_BY_LANG: Record<string, string> = {
  en: 'en-US',
  ar: 'ar',
  ur: 'ur',
  hi: 'hi-IN',
  bn: 'bn-IN',
  si: 'si-LK',
  ne: 'ne-NP',
};

export function numberLocale(lang?: string | null): string {
  const base = (lang || 'en').split('-')[0].toLowerCase();
  return `${LOCALE_BY_LANG[base] ?? 'en-US'}-u-nu-latn`;
}

const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(lang: string | null | undefined, options: Intl.NumberFormatOptions) {
  const locale = numberLocale(lang);
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Strip bidi control marks some locales emit (keeps output stable inside `<bdi>`). */
function clean(text: string): string {
  return text.replace(/[‎‏؜]/g, '');
}

export function formatNumber(
  value: number | null | undefined,
  lang?: string | null,
  options: Intl.NumberFormatOptions = { maximumFractionDigits: 2 },
): string {
  if (!isNumber(value)) return EMPTY_VALUE;
  return clean(getFormatter(lang, options).format(value)).replace('-', MINUS);
}

export interface ParsedFigure {
  prefix: string;
  value: number;
  suffix: string;
  /** Options that reproduce the original digits (decimals, grouping). */
  options: Intl.NumberFormatOptions;
}

const FIGURE_PATTERN = /^([^\d−-]*)([−-]?\d[\d,]*(?:\.\d+)?)(\D*)$/;

/**
 * Reads a figure produced by `formatNumber` / `formatMetricValue` back into a
 * number plus the options that re-create it, so it can be counted up and
 * re-formatted frame by frame. Returns null for anything that does not
 * round-trip exactly ("—", ranges, compact strings with digits in the suffix…).
 */
export function parseFigure(text: string, lang?: string | null): ParsedFigure | null {
  const match = FIGURE_PATTERN.exec(text);
  if (!match) return null;
  const [, prefix, numeral, suffix] = match;
  const negative = /^[−-]/.test(numeral);
  const [int, frac = ''] = numeral.replace(/^[−-]/, '').split('.');
  const value = (negative ? -1 : 1) * Number(`${int.replace(/,/g, '')}${frac ? `.${frac}` : ''}`);
  if (!Number.isFinite(value)) return null;
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: frac.length,
    maximumFractionDigits: frac.length,
    useGrouping: int.includes(',') || int.length < 4,
  };
  if (formatNumber(value, lang, options) !== numeral.replace('-', MINUS)) return null;
  return { prefix, value, suffix, options };
}

/** Compact axis ticks: 12,400 → "12.4K" (locale-aware suffix). */
export function formatCompact(value: number | null | undefined, lang?: string | null): string {
  if (!isNumber(value)) return '';
  if (Math.abs(value) < 1000) return formatNumber(value, lang, { maximumFractionDigits: 1 });
  return formatNumber(value, lang, { notation: 'compact', maximumFractionDigits: 1 });
}

export type MetricValueKind =
  | 'mass_kg'
  | 'mass_t'
  | 'tco2e'
  | 'percent'
  | 'count'
  | 'hours'
  | 'sar'
  | 'sar_per_kg'
  | 'kwh'
  | 'litres'
  | 'm3'
  | 'decimal';

export interface FormattedMetric {
  /** Formatted figure (no unit). `—` when the value is missing. */
  text: string;
  /** i18n key for the unit (e.g. `units.kg`), or null when unitless. */
  unitKey: string | null;
  /** True when the value was missing / not finite. */
  empty: boolean;
}

export interface FormatMetricOptions {
  /** Show masses ≥ 1,000 kg in tonnes (hero figures). Default false. */
  scaleMass?: boolean;
}

/** tCO2e precision ladder: small estimates keep their significant digits. */
export function tco2eFormatOptions(value: number): Intl.NumberFormatOptions {
  const abs = Math.abs(value);
  if (abs === 0) return { maximumFractionDigits: 0 };
  if (abs < 0.01) return { maximumFractionDigits: 4 };
  if (abs < 1) return { maximumSignificantDigits: 3 };
  if (abs < 100) return { maximumFractionDigits: 2 };
  return { maximumFractionDigits: 1 };
}

function massOptions(value: number): Intl.NumberFormatOptions {
  return { maximumFractionDigits: Math.abs(value) < 10 ? 1 : 0 };
}

const UNIT_KEY: Record<MetricValueKind, string | null> = {
  mass_kg: 'units.kg',
  mass_t: 'units.tonnes',
  tco2e: 'units.tco2e',
  percent: 'units.percent',
  count: null,
  hours: 'units.hours',
  sar: 'units.sar',
  sar_per_kg: 'units.sarPerKg',
  kwh: 'units.kwh',
  litres: 'units.liters',
  m3: 'units.m3',
  decimal: null,
};

export function formatMetricValue(
  kind: MetricValueKind,
  value: number | null | undefined,
  lang?: string | null,
  { scaleMass = false }: FormatMetricOptions = {},
): FormattedMetric {
  if (!isNumber(value)) {
    return { text: EMPTY_VALUE, unitKey: UNIT_KEY[kind], empty: true };
  }

  switch (kind) {
    case 'mass_kg': {
      if (scaleMass && Math.abs(value) >= 1000) {
        const tonnes = value / 1000;
        return {
          text: formatNumber(tonnes, lang, { maximumFractionDigits: Math.abs(tonnes) < 100 ? 2 : 1 }),
          unitKey: 'units.tonnes',
          empty: false,
        };
      }
      return { text: formatNumber(value, lang, massOptions(value)), unitKey: 'units.kg', empty: false };
    }
    case 'mass_t':
      return {
        text: formatNumber(value, lang, { maximumFractionDigits: 2 }),
        unitKey: 'units.tonnes',
        empty: false,
      };
    case 'tco2e':
      return { text: formatNumber(value, lang, tco2eFormatOptions(value)), unitKey: 'units.tco2e', empty: false };
    case 'percent':
    case 'hours':
      return { text: formatNumber(value, lang, { maximumFractionDigits: 1 }), unitKey: UNIT_KEY[kind], empty: false };
    case 'count':
      return { text: formatNumber(value, lang, { maximumFractionDigits: 0 }), unitKey: null, empty: false };
    case 'sar':
    case 'sar_per_kg':
      return { text: formatNumber(value, lang, { maximumFractionDigits: 2 }), unitKey: UNIT_KEY[kind], empty: false };
    case 'kwh':
    case 'litres':
    case 'm3':
      return { text: formatNumber(value, lang, massOptions(value)), unitKey: UNIT_KEY[kind], empty: false };
    case 'decimal':
    default:
      return { text: formatNumber(value, lang, { maximumFractionDigits: 2 }), unitKey: null, empty: false };
  }
}

/**
 * Share of a whole (fraction 0..1) as a percentage string. Tiny non-zero
 * shares render as "<0.1%" instead of a misleading "0%".
 */
export function formatShare(fraction: number | null | undefined, lang?: string | null): string {
  if (!isNumber(fraction)) return EMPTY_VALUE;
  const pct = fraction * 100;
  if (pct > 0 && pct < 0.1) return `<${formatNumber(0.1, lang, { maximumFractionDigits: 1 })}%`;
  return `${formatNumber(pct, lang, { maximumFractionDigits: pct < 10 ? 1 : 0 })}%`;
}

export type DeltaKind = 'relative' | 'points';

/**
 * Signed change for a delta row. `relative` → "+12.8%" (percent change);
 * `points` → "+3.2" (caller appends the localized "pp" / "نقطة" suffix).
 * Returns null when the delta is not computable.
 */
export function formatDelta(
  delta: number | null | undefined,
  kind: DeltaKind,
  lang?: string | null,
): string | null {
  if (!isNumber(delta)) return null;
  const abs = Math.abs(delta);
  const body = formatNumber(abs, lang, { maximumFractionDigits: abs < 10 ? 1 : 0 });
  const sign = delta > 0 ? '+' : delta < 0 ? MINUS : '';
  return `${sign}${body}${kind === 'relative' ? '%' : ''}`;
}

/**
 * Size of a change for display next to an up/down arrow: "100%", "12.8%" —
 * no sign (the arrow carries the direction) and a percent sign for both
 * relative changes and rate (percentage-point) changes.
 */
export function formatDeltaMagnitude(delta: number | null | undefined, lang?: string | null): string | null {
  if (!isNumber(delta)) return null;
  const abs = Math.abs(delta);
  return `${formatNumber(abs, lang, { maximumFractionDigits: abs < 10 ? 1 : 0 })}%`;
}

/** Date locale: the UI language with Latin digits and the Gregorian calendar. */
export function dateLocale(lang?: string | null): string {
  return `${numberLocale(lang).replace(/-u-nu-latn$/, '')}-u-ca-gregory-nu-latn`;
}

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getDateFormatter(lang: string | null | undefined, options: Intl.DateTimeFormatOptions) {
  const locale = dateLocale(lang);
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = dateFormatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateFormatterCache.set(key, formatter);
  }
  return formatter;
}

function toValidDate(value: Date | number | string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Locale date/time with Latin digits; `—` for missing or invalid input. */
export function formatDateTime(
  value: Date | number | string | null | undefined,
  lang?: string | null,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' },
): string {
  const date = toValidDate(value);
  if (!date) return EMPTY_VALUE;
  return clean(getDateFormatter(lang, options).format(date));
}

/** "July 2026" / "يوليو 2026". */
export function formatMonth(value: Date | number | string | null | undefined, lang?: string | null): string {
  return formatDateTime(value, lang, { month: 'long', year: 'numeric' });
}

/** "Tue, 6 Oct" — day labels for the fleet day stepper. */
export function formatDay(value: Date | number | string | null | undefined, lang?: string | null): string {
  return formatDateTime(value, lang, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** "08:14" — 24-hour clock times for operational events. */
export function formatClock(value: Date | number | string | null | undefined, lang?: string | null): string {
  return formatDateTime(value, lang, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}

const RELATIVE_STEPS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.35],
  ['month', 12],
  ['year', Number.POSITIVE_INFINITY],
];

/**
 * "20 seconds ago", "3 minutes ago", "in 2 hours" — for freshness labels.
 * Only for real timestamps (a fetch time or a server `updated_at`).
 */
export function formatRelative(
  value: Date | number | string | null | undefined,
  now: Date | number,
  lang?: string | null,
): string {
  const date = toValidDate(value);
  if (!date) return EMPTY_VALUE;
  let amount = (date.getTime() - (now instanceof Date ? now.getTime() : now)) / 1000;
  let unit: Intl.RelativeTimeFormatUnit = 'second';
  for (const [stepUnit, size] of RELATIVE_STEPS) {
    unit = stepUnit;
    if (Math.abs(amount) < size) break;
    amount /= size;
  }
  const rounded = Math.round(amount);
  const formatter = new Intl.RelativeTimeFormat(numberLocale(lang), { numeric: 'auto' });
  return clean(formatter.format(rounded === 0 ? 0 : rounded, unit));
}
